import { create } from "zustand";
import { persist } from "zustand/middleware";
import { type UserScope } from "@/entities/auth";

interface UserScopeState {
  scope: UserScope | null;
  setScope: (scope: UserScope) => void;
  clearScope: () => void;
  hasModuleAccess: (moduleName: string) => boolean;
  hasSubjectAccess: (subject: string) => boolean;
  canAccessMenuItem: (menuKey: string) => boolean;
  /** Check if the user has a specific action on a subject (e.g. CREATE on AssetCategory) */
  can: (subject: string, action: string) => boolean;
}

// Mapping between menu keys and their corresponding subjects/modules
const MENU_TO_SUBJECT_MAP: Record<string, string[]> = {
  dashboard: [], // Always accessible
  technicianDashboard: [], // Always accessible
  events: ["Event"],
  branches: ["Location"],
  branchCategories: ["Location"],
  assets: ["Asset"],
  assetCategories: ["AssetCategory"],
  assetModels: ["AssetModel"],
  maintenance: ["Maintenance"],
  inspections: ["Maintenance", "Asset"], // Can be accessed via either
  parts: ["Part"],
  warehouses: ["Warehouse"],
  suppliers: ["Supplier"],
  tickets: ["Ticket"],
  issueCategories: ["ProblemCategory"],
  ticketQueues: ["Queue"],
  analysis: [], // Might need special handling
  people: ["Contact"],
  users: ["User"],
  policies: ["Policy"],
  settings: ["Settings"],
};

// Mapping between menu keys and module names (module names from backend)
const MENU_TO_MODULE_MAP: Record<string, string[]> = {
  events: ["events"],
  branches: ["branches"],
  assets: ["assets"],
  maintenance: ["maintenance"],
  inspections: ["maintenance", "inspections"],
  parts: ["parts"],
  warehouses: ["warehouses"],
  suppliers: ["suppliers"],
  tickets: ["tickets"],
  analysis: ["analysis"],
  people: ["people"],
  users: ["users"],
  policies: ["users"], // Policies are managed under users module
  settings: ["settings"],
  // Child menu items inherit parent module
  branchCategories: ["branches"],
  assetCategories: ["assets"],
  assetModels: ["assets"],
  issueCategories: ["tickets"],
  ticketQueues: ["tickets"],
};

// Child menu keys to their parent menu key – children are shown if user has access to parent
const MENU_CHILD_TO_PARENT: Record<string, string> = {
  branchCategories: "branches",
  assetCategories: "assets",
  assetModels: "assets",
  warehouses: "parts",
  suppliers: "parts",
  issueCategories: "tickets",
  ticketQueues: "tickets",
};

export const useUserScopeStore = create<UserScopeState>()(
  persist(
    (set, get) => ({
      scope: null,
      setScope: (scope) => set({ scope }),
      clearScope: () => set({ scope: null }),

      hasModuleAccess: (moduleName: string) => {
        const { scope } = get();
        if (!scope) return false;
        // Module access is account-level — not bypassed by subject permissions
        return scope.enabledModules.includes(moduleName);
      },

      hasSubjectAccess: (subject: string) => {
        const { scope } = get();
        if (!scope) return false;
        // If user has 'all' subject, they have access to everything
        if (scope.allowedSubjects.includes("all")) return true;
        return scope.allowedSubjects.includes(subject);
      },

      canAccessMenuItem: (menuKey: string) => {
        const { scope } = get();
        if (!scope) return false;

        // Get subject and module requirements for this menu item
        const subjects = MENU_TO_SUBJECT_MAP[menuKey] || [];
        const modules = MENU_TO_MODULE_MAP[menuKey] || [];

        // Special case: dashboard and technicianDashboard have no requirements
        if (menuKey === "dashboard" || menuKey === "technicianDashboard") {
          return true;
        }

        // Module access is an account-level entitlement — always enforced,
        // even for users with full subject access ('all').
        const hasModuleAccess =
          modules.length > 0
            ? modules.some((moduleName) => scope.enabledModules.includes(moduleName))
            : false;

        if (!hasModuleAccess) {
          const parentKey = MENU_CHILD_TO_PARENT[menuKey];
          if (parentKey && get().canAccessMenuItem(parentKey)) {
            return true;
          }
          return false;
        }

        // Module is enabled. Now check subject-level permissions.
        // Users with 'all' subject can access any enabled module.
        if (scope.allowedSubjects.includes("all")) return true;

        if (subjects.length === 0) {
          return true;
        }

        return subjects.some((subject) => scope.allowedSubjects.includes(subject));
      },

      can: (subject: string, action: string) => {
        const { scope } = get();
        if (!scope?.allowedPermissions?.length) return false;
        const perms = scope.allowedPermissions;
        const hasAllManage = perms.some(
          (p) => p.subject === "all" && (p.action === "manage" || p.action === action)
        );
        if (hasAllManage) return true;
        const hasSubjectManage = perms.some((p) => p.subject === subject && p.action === "manage");
        if (hasSubjectManage) return true;
        return perms.some((p) => p.subject === subject && p.action === action);
      },
    }),
    {
      name: "user-scope-storage",
      partialize: (state) => ({
        scope: state.scope,
      }),
    }
  )
);
